import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Formik, FastField } from 'formik';
import * as Yup from 'yup';

import FormInput from '../FormInput';
import FormAutocomplete from '../FormAutocomplete';
import { adminUsers as adminUsersActions } from '../../../actionTypes';
import useStyles from './styles';

const SUSPENSION_REASON_MAX_LENGTH = 255;

const suspensionSchema = Yup.object().shape({
  reason: Yup.string()
    .required('Suspension reason is required')
    .max(SUSPENSION_REASON_MAX_LENGTH, `Reason must be at most ${SUSPENSION_REASON_MAX_LENGTH} characters`),
  coordinatorUserId: Yup.number()
    .required('iPQF Coordinator is required')
    .typeError('Please select a coordinator'),
});

const SuspensionModal = ({ open, onClose, onSubmit, loading }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { users: adminUsers, loading: loadingAdmins } = useSelector(store => store.adminUsers);

  useEffect(() => {
    if (open) {
      dispatch({ type: adminUsersActions.get.start });
    }
  }, [open, dispatch]);

  const handleSubmit = (values, { resetForm }) => {
    onSubmit(values);
    resetForm();
  };

  const coordinatorOptions = adminUsers.map(user => ({
    value: user.id,
    label: `${user.firstName} ${user.lastName} - ${user.emailAddress}`,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Suspend Contractor</DialogTitle>
      <Formik
        initialValues={{ reason: '', coordinatorUserId: '' }}
        validationSchema={suspensionSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleSubmit, isValid, dirty }) => (
          <>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" className={classes.description}>
                    Please provide a reason for the suspension and select the iPQF Coordinator for the contractor to
                    contact.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FastField
                    name="reason"
                    label="Suspension Reason *"
                    placeholder="Suspension Reason"
                    component={FormInput}
                    multiline
                    rows={4}
                    inputProps={{ maxLength: SUSPENSION_REASON_MAX_LENGTH }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FastField
                    name="coordinatorUserId"
                    label="Select iPQF Coordinator *"
                    component={FormAutocomplete}
                    options={coordinatorOptions}
                    disabled={loadingAdmins}
                  />
                  {loadingAdmins && (
                    <Typography variant="caption" color="textSecondary">
                      Loading coordinators...
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Button onClick={onClose} color="default" disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                color="secondary"
                variant="contained"
                disabled={!isValid || !dirty || loading || loadingAdmins}
              >
                {loading ? <CircularProgress size={24} /> : 'Suspend Contractor'}
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default SuspensionModal;
